<template>
  <div class="checker">
    <form class="checker__bar" @submit.prevent="run">
      <span class="checker__prompt">&gt;_</span>
      <input v-model.trim="addr" :placeholder="c.tools.placeholder" spellcheck="false" autocomplete="off" aria-label="TRON address">
      <button class="btn btn--primary" type="submit" :disabled="loading">{{ loading ? c.tools.checking : c.tools.button }}</button>
    </form>

    <transition name="fade">
      <div v-if="result" class="checker__result" :class="result.isRisk ? 'is-risk' : 'is-safe'">
        <div class="checker__verdict">
          <span class="checker__badge">{{ result.isRisk ? c.tools.badgeRisk : c.tools.badgeSafe }}</span>
          <p>{{ result.isRisk ? c.tools.risk : c.tools.safe }}</p>
        </div>
        <dl class="checker__grid">
          <div class="span-3"><dt>{{ c.tools.address }}</dt><dd class="mono">{{ result.address }}</dd></div>
          <div><dt>{{ c.tools.usdt }}</dt><dd>{{ result.usdt }}</dd></div>
          <div><dt>{{ c.tools.trx }}</dt><dd>{{ result.trx }}</dd></div>
          <div><dt>{{ c.tools.tokens }}</dt><dd>{{ result.tokens }}</dd></div>
        </dl>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { content as c } from '@/i18n'
import { TRON_ADDRESS_RE, checkAddress } from '@/tron'
import { toast } from '@/toast'
import { track } from '@/track'

const addr = ref('')
const loading = ref(false)
const result = ref(null)

async function run() {
  if (!addr.value) return toast(c.value.tools.errEmpty, 'error')
  if (!TRON_ADDRESS_RE.test(addr.value)) return toast(c.value.tools.errFormat, 'error')
  if (loading.value) return
  loading.value = true
  result.value = null
  try {
    result.value = await checkAddress(addr.value)
    track('usdt_check', { label: result.value.isRisk ? 'frozen' : 'clear' })
  } catch (e) {
    const bad = /address|checksum|base58/.test(String(e && e.message))
    toast(bad ? c.value.tools.errFormat : c.value.tools.errFetch, 'error')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.checker { max-width: 820px; margin: 0 auto; }
.checker__bar {
  display: flex; align-items: center; gap: 0;
  background: var(--ink-2); border: 1px solid rgba(255,255,255,.14);
  transition: border-color .2s;
}
.checker__bar:focus-within { border-color: var(--accent); }
.checker__prompt { font-family: var(--font-mono); color: var(--accent); padding: 0 6px 0 20px; }
.checker__bar input {
  flex: 1; min-width: 0; height: 60px; background: transparent; border: 0; outline: none;
  font-family: var(--font-mono); font-size: 15px; color: var(--ink-text); padding: 0 12px;
}
.checker__bar input::placeholder { color: var(--ink-dim); }
.checker__bar .btn { height: 60px; clip-path: none; }
.checker__bar .btn:disabled { opacity: .6; cursor: progress; }

.checker__result { margin-top: 20px; border: 1px solid var(--ink-line); background: var(--ink-2); }
.checker__verdict { display: flex; gap: 16px; align-items: flex-start; padding: 22px 24px; border-bottom: 1px solid var(--ink-line); }
.checker__verdict p { font-size: 15px; }
.checker__badge { font-family: var(--font-mono); font-size: 11px; letter-spacing: .14em; padding: 4px 8px; border: 1px solid; white-space: nowrap; }
.is-risk .checker__badge { color: var(--danger); border-color: rgba(255,77,61,.5); }
.is-risk .checker__verdict p { color: #ffc2bb; }
.is-safe .checker__badge { color: var(--ok); border-color: rgba(51,194,127,.5); }
.is-safe .checker__verdict p { color: var(--ink-muted); }
.checker__grid { display: grid; grid-template-columns: repeat(3, 1fr); margin: 0; }
.checker__grid > div { padding: 18px 24px; border-right: 1px solid var(--ink-line); border-top: 1px solid var(--ink-line); }
.checker__grid > div:nth-child(1) { border-top: 0; }
.checker__grid > div:last-child { border-right: 0; }
.checker__grid .span-3 { grid-column: span 3; border-right: 0; }
.checker__grid dt { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; color: var(--ink-dim); text-transform: uppercase; }
.checker__grid dd { margin: 6px 0 0; font-family: var(--font-display); font-size: 22px; color: var(--ink-text); }
.checker__grid dd.mono { font-family: var(--font-mono); font-size: 14px; word-break: break-all; }

.fade-enter-active, .fade-leave-active { transition: opacity .3s, transform .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(8px); }

@media (max-width: 600px) {
  .checker__bar { flex-wrap: wrap; }
  .checker__bar input { height: 54px; }
  .checker__bar .btn { width: 100%; height: 50px; }
  .checker__grid { grid-template-columns: 1fr; }
  .checker__grid .span-3 { grid-column: auto; }
  .checker__grid > div { border-right: 0; }
}
</style>
